/**
 * Authentication API service
 * 
 * Handles communication with WordPress JWT authentication endpoints.
 */

import axios, { AxiosError } from 'axios';
import type { AuthSession, JWTPayload } from '../utils/auth';
import { decodeJWT } from '../utils/auth';

// API base URL - will be configured via environment variable
const API_BASE_URL = import.meta.env.VITE_WP_API_URL || '';

export interface LoginCredentials {
  username: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  user_email: string;
  user_nicename: string;
  user_display_name: string;
}

export interface AuthError {
  code: string;
  message: string;
  status: number;
}

/**
 * Authenticate user with WordPress JWT endpoint
 * 
 * @param credentials - Username and password
 * @returns AuthSession on success
 * @throws AuthError on failure
 */
export async function login(credentials: LoginCredentials): Promise<AuthSession> {
  try {
    const response = await axios.post<LoginResponse>(
      `${API_BASE_URL}/wp-json/jwt-auth/v1/token`,
      credentials,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    const { token, user_display_name } = response.data;
    
    // Decode the token to get user info and expiration
    const payload = decodeJWT(token);
    if (!payload) {
      throw {
        code: 'invalid_token',
        message: 'Failed to decode authentication token',
        status: 500,
      } as AuthError;
    }

    // Create session object
    const session: AuthSession = {
      token,
      user: {
        id: payload.data.user.id,
        username: payload.data.user.username,
        displayName: user_display_name,
      },
      expiresAt: payload.exp,
    };

    return session;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<{ code: string; message: string }>;
      throw {
        code: axiosError.response?.data?.code || 'unknown_error',
        message: axiosError.response?.data?.message || 'Authentication failed',
        status: axiosError.response?.status || 500,
      } as AuthError;
    }
    throw error;
  }
}

/**
 * Validate an existing JWT token
 * 
 * @param token - JWT token to validate
 * @returns true if valid, false otherwise
 */
export async function validateTokenWithServer(token: string): Promise<boolean> {
  try {
    await axios.post(
      `${API_BASE_URL}/wp-json/jwt-auth/v1/token/validate`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return true;
  } catch {
    return false;
  }
}

/**
 * Get JWT payload from token
 */
export function getTokenPayload(token: string): JWTPayload | null {
  return decodeJWT(token);
}
